// src/utils/clinicalInterviewWorkflow.js
// Utilities to support organisation/patient-specific Clinical Interview (CI) workflow customisation.
// Business intent: allow certain organisations to exclude specific CI sections (by title/varName)
// without migrating stored data. The UI, navigation, progress, and generation logic should treat
// excluded sections as if they do not exist for that patient.
//
// Org-level exclusions (e.g. Psychological Consultations skipping MSE) and deferred-DF
// exclusions are now resolved via the centralised workflow config (resolveWorkflowConfig)
// instead of inline org-name checks and the legacy shouldDeferDF function.

import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';

/**
 * Get organisation-level default CI exclusions by section title.
 * Derives exclusions from the centralised workflow config so that patients with
 * a stamped workflowConfig use their stored values while legacy patients fall
 * back to org-name string matching via deriveLegacyConfig.
 *
 * When DF is deferred AND the org does NOT use CI Review & Synthesis,
 * "Diagnostic Formulation" is excluded from the CI workflow entirely — it
 * becomes part of the SSI flow instead.  This ensures the side navbar,
 * progress calculations, and section generation loops all treat RSK as the
 * final CI section automatically.
 *
 * When hasCIReview is true (e.g. SLaM), section 12 remains in CI but renders
 * as "Review & Synthesis" — it is NOT excluded because the Lead clinician
 * still completes CI through that page.
 */
export function getOrgCIExcludedSectionTitles(organisationName) {
  const wf = resolveWorkflowConfig(null, { organisation: organisationName });
  const exclusions = [...(wf.ciExclusions || [])];
  if (wf.isDFDeferred && !wf.hasCIReview) {
    exclusions.push('Diagnostic Formulation');
  }
  return exclusions;
}

/**
 * Resolve excluded CI section titles for a patient.
 * Priority: patient doc overrides -> workflow-config-derived org defaults.
 *
 * When a patient document exists, resolveWorkflowConfig can use its stamped
 * workflowConfig (for new patients) or fall back to deriveLegacyConfig.
 */
export function getExcludedCITitles(patientDataModel, organisationName) {
  const patientTitles = patientDataModel?.ciExcludedSectionTitles;
  if (Array.isArray(patientTitles) && patientTitles.length > 0) {
    return patientTitles;
  }
  // Use workflow config with patient data so stamped configs are honoured
  const wf = resolveWorkflowConfig(patientDataModel, { organisation: organisationName });
  const exclusions = [...(wf.ciExclusions || [])];
  if (wf.isDFDeferred && !wf.hasCIReview) {
    exclusions.push('Diagnostic Formulation');
  }
  return exclusions;
}

/**
 * Resolve excluded CI section varNames for a patient if present (optional extension).
 * This supports future-proofing if we choose to store varName-based exclusions.
 */
export function getExcludedCIVarNames(patientDataModel) {
  const patientVarNames = patientDataModel?.ciExcludedSectionVarNames;
  return Array.isArray(patientVarNames) ? patientVarNames : [];
}

/**
 * Given CI sections map and the exclusion lists, return the set of excluded keys.
 * Sections are keyed as string numbers ('1'..'12').
 */
export function resolveExcludedCIKeys(ciSectionsMap, excludedTitles = [], excludedVarNames = []) {
  if (!ciSectionsMap) return new Set();
  const excluded = new Set();
  Object.entries(ciSectionsMap).forEach(([key, section]) => {
    const title = section?.sectionTitle;
    const varName = section?.sectionVarName;
    if ((title && excludedTitles.includes(title)) || (varName && excludedVarNames.includes(varName))) {
      excluded.add(key);
    }
  });
  return excluded;
}

/**
 * Return an ordered array of visible CI sections [{ key, ...section }], excluding hidden ones.
 */
export function getVisibleCISections(ciSectionsMap, excludedKeysSet) {
  if (!ciSectionsMap) return [];
  return Object.entries(ciSectionsMap)
    .filter(([key]) => !excludedKeysSet?.has(key))
    .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
    .map(([key, section]) => ({ key, ...section }));
}

/**
 * Return visible CI section titles in order.
 */
export function getVisibleCISectionTitles(ciSectionsMap, excludedKeysSet) {
  return getVisibleCISections(ciSectionsMap, excludedKeysSet).map(s => s.sectionTitle);
}

/**
 * Find the previous and next section keys from an ordered visible keys array.
 */
export function getPrevNextCISectionKeys(visibleKeys, currentKey) {
  const idx = visibleKeys.indexOf(`${currentKey}`);
  const prevKey = idx > 0 ? visibleKeys[idx - 1] : null;
  const nextKey = idx >= 0 && idx < visibleKeys.length - 1 ? visibleKeys[idx + 1] : null;
  return { prevKey, nextKey };
}

/**
 * Compute CI average progress over visible sections only.
 */
export function computeCIProgress(ciSectionsMap, excludedKeysSet) {
  const visible = getVisibleCISections(ciSectionsMap, excludedKeysSet);
  if (visible.length === 0) return 0;
  const total = visible.reduce((sum, s) => sum + (s.progress || 0), 0);
  return Math.round(total / visible.length);
}

/**
 * Convenience: resolve all exclusion context from patient data.
 */
export function buildCIExclusionsContext(patientDataModel, organisationName) {
  const ci = patientDataModel?.dataModel?.['2_clinicalInterview'];
  const sections = ci?.sections || {};
  const excludedTitles = getExcludedCITitles(patientDataModel, organisationName);
  const excludedVarNames = getExcludedCIVarNames(patientDataModel);
  const excludedKeys = resolveExcludedCIKeys(sections, excludedTitles, excludedVarNames);
  return { sections, excludedTitles, excludedVarNames, excludedKeys };
}

/**
 * Check whether a CI section is excluded by matching title or varName.
 */
export function isCISectionExcludedByIdentity(patientDataModel, organisationName, sectionTitle, sectionVarName) {
  const { excludedTitles, excludedVarNames } = buildCIExclusionsContext(patientDataModel, organisationName);
  if (sectionTitle && excludedTitles.includes(sectionTitle)) return true;
  if (sectionVarName && excludedVarNames.includes(sectionVarName)) return true;
  return false;
}

/**
 * Map canonical CI section titles to user-facing display titles.
 * Keeps legacy dataModel titles intact while presenting the preferred wording in UI.
 * Accepts an optional workflow config object to handle org-specific title overrides
 * (e.g. SLaM CI Review orgs see "Review & Synthesis" instead of "Diagnostic Formulation").
 */
export function mapCIDisplayTitle(title, wf) {
  if (title === 'Mental Status Examination') return 'Mental State Examination';
  if (title === 'Diagnostic Formulation' && wf?.hasCIReview) return 'Review & Synthesis';
  return title;
}


